import { call, put, select, takeEvery, takeLeading } from 'redux-saga/effects';
import { apiServiceAsync, TReplayParams, TReplaySetOptionsParams } from '../services/apiService';
import { replayListSelector, replaysOptionsSelector, selectedRegionSelector } from './selectors';
import { IReplayListState, IReplaysOptions } from '../libs/store';
import { actions, actionTypes } from './modules';
import { TAction } from '../../../../../shared/frontend/scripts/actions/types/action';
import { IReplayAddonData } from '../../../shared/replaysManagementShared';
import { TUserKeystoneReplayWatchSetSummary } from '../../../shared/types/replaySetTypes';

const { setLoading, setLoadingKeystoneRun, setFailure, setSelectedRegion, setReplayListFetchStatus, loadReplayList, loadReplaysOptions } = actions;

function* addReplaySaga(action: TAction<TReplayParams>): any {
  const replayList: IReplayListState = yield select(replayListSelector);

  try {
    yield put(setLoadingKeystoneRun({ loadingKeystoneRunId: action.payload.keystoneRunId }));
    const { replay } = yield call(apiServiceAsync.addReplay, action.payload);

    yield put(
      loadReplayList({
        replayList: {
          isLoaded: true,
          list: [...replayList.list, replay],
          fetchStatus: 'success'
        }
      })
    );
  } catch (e) {
    yield put(setFailure(e.message));
  } finally {
    yield put(setLoadingKeystoneRun({ loadingKeystoneRunId: null }));
  }
}

function* removeReplaySaga(action: TAction<TReplayParams>): any {
  const replayList: IReplayListState = yield select(replayListSelector);

  try {
    yield put(setLoadingKeystoneRun({ loadingKeystoneRunId: action.payload.keystoneRunId }));
    const { result } = yield call(apiServiceAsync.removeReplay, action.payload);
    if (result) {
      yield put(
        loadReplayList({
          replayList: {
            isLoaded: true,
            list: replayList.list.filter(({ run, season }: IReplayAddonData) => {
              return `${run!.keystone_run_id}-${season}` !== `${action.payload.keystoneRunId}-${action.payload.season}`;
            }),
            fetchStatus: 'success'
          }
        })
      );
    }
  } catch (e) {
    yield put(setFailure(e.message));
  } finally {
    yield put(setLoadingKeystoneRun({ loadingKeystoneRunId: null }));
  }
}

function* fetchReplayListSaga(action: TAction<string>): any {
  const selectedRegion = yield select(selectedRegionSelector);

  try {
    yield put(setReplayListFetchStatus('loading'));
    const { replays } = yield call(apiServiceAsync.getReplayList, { region: action.payload });

    yield put(
      loadReplayList({
        replayList: {
          isLoaded: true,
          list: replays,
          fetchStatus: 'success'
        }
      })
    );

    if (selectedRegion !== action.payload) {
      yield put(setSelectedRegion({
        selectedRegion: action.payload
      }));
    }
  } catch (e) {
    yield put(setFailure(e.message));
    yield put(setReplayListFetchStatus('error'));
  }
}

function* fetchReplaysOptionsSaga(): any {
  try {
    yield put(setLoading({ isLoading: true }));
    const { replaysOptions } = yield call(apiServiceAsync.getReplaysOptions);

    yield put(
      loadReplaysOptions({
        replaysOptions: {
          isLoaded: true,
          options: replaysOptions
        }
      })
    );
  } catch (e) {
    yield put(setFailure(e.message));
  } finally {
    yield put(setLoading({ isLoading: false }));
  }
}

function* saveReplaysOptionsSaga(action: TAction<Partial<Omit<IReplaysOptions, 'replays_management_watch_sets'>>>): any {
  const selectedRegion = yield select(selectedRegionSelector);
  const replaysOptions = yield select(replaysOptionsSelector);

  try {
    yield put(setLoading({ isLoading: true }));
    const { result } = yield call(apiServiceAsync.saveReplaysOptions, action.payload);

    if (result) {
      yield put(
        loadReplaysOptions({
          replaysOptions: {
            isLoaded: true,
            options: {
              ...(replaysOptions.options || {}),
              ...action.payload
            }
          }
        })
      );

      const { replays } = yield call(apiServiceAsync.getReplayList, selectedRegion);

      yield put(
        loadReplayList({
          replayList: {
            isLoaded: true,
            list: replays,
            fetchStatus: 'success'
          }
        })
      );
    }
  } catch (e) {
    yield put(setFailure(e.message));
  } finally {
    yield put(setLoading({ isLoading: false }));
  }
}

function* saveReplaySetOptionsSaga(action: TAction<TReplaySetOptionsParams>): any {
  const selectedRegion = yield select(selectedRegionSelector);
  const replaysOptions = yield select(replaysOptionsSelector);

  try {
    yield put(setLoading({ isLoading: true }));
    const { result } = yield call(apiServiceAsync.saveReplaySetOptions, action.payload);

    if (result) {
      yield put(
        loadReplaysOptions({
          replaysOptions: {
            isLoaded: true,
            options: {
              ...(replaysOptions.options || {}),
              replays_management_watch_sets: replaysOptions.options.replays_management_watch_sets.map((userReplaySet: TUserKeystoneReplayWatchSetSummary) => {
                if (action.payload.keystoneReplaySetId === userReplaySet.keystoneReplaySet.id) {
                  return {
                    ...userReplaySet,
                    isActive: action.payload.isActive
                  }
                }

                return userReplaySet;
              }),
            }
          }
        })
      );

      const { replays } = yield call(apiServiceAsync.getReplayList, selectedRegion);

      yield put(
        loadReplayList({
          replayList: {
            isLoaded: true,
            list: replays,
            fetchStatus: 'success'
          }
        })
      );
    }
  } catch (e) {
    yield put(setFailure(e.message));
  } finally {
    yield put(setLoading({ isLoading: false }));
  }
}

function* watchRequestAddReplay(): any {
  yield takeEvery(actionTypes.ADD_REPLAY, addReplaySaga);
}

function* watchRequestRemoveReplay(): any {
  yield takeEvery(actionTypes.REMOVE_REPLAY, removeReplaySaga);
}

function* watchFetchReplayList(): any {
  yield takeLeading(actionTypes.GET_REPLAY_LIST, fetchReplayListSaga);
}

function* watchFetchReplaysConfig(): any {
  yield takeEvery(actionTypes.GET_REPLAYS_OPTIONS, fetchReplaysOptionsSaga);
}

function* watchRequestReplaysOptions(): any {
  yield takeEvery(actionTypes.SAVE_REPLAYS_OPTIONS, saveReplaysOptionsSaga);
}

function* watchRequestReplaySetOptions(): any {
  yield takeEvery(actionTypes.SAVE_REPLAY_SET_OPTIONS, saveReplaySetOptionsSaga);
}

export default [
  watchRequestAddReplay(),
  watchRequestRemoveReplay(),
  watchFetchReplayList(),
  watchFetchReplaysConfig(),
  watchRequestReplaysOptions(),
  watchRequestReplaySetOptions()
];
